import React, { useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { NumberInput } from 'components/ucl';
import { logButtonClick, logRBIEvent } from 'state/amplitude';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/amplitude/constants';
import { selectors, useAppSelector } from 'state/global-state';
import { MAX_CART_QUANTITY, MIN_CART_QUANTITY } from 'utils/cart';
import { HapticsImpactStyle } from 'utils/haptic/types';
import { routes } from 'utils/routing';

import { useProductWizardContext } from '../../product-wizard';
import { useProductCart } from '../../product-wizard/use-product-cart';
import { useProductTotalPrice } from '../../product-wizard/use-product-total-price';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';

import { displayValueStyleProps, incrementorStyleProps } from './styled';

export const AddToCartCta = () => {
  const { formatMessage } = useIntl();
  const { navigate } = useRouter();

  const {
    selectedProduct,
    slotsSelections,
    productQuantity,
    setProductQuantity,
  } = useProductWizardContext();
  const { cartIdEditing, closeWizard, rewardBenefitId, offerId } = useProductWizardFlow();
  const { addToCart } = useProductCart();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  const quantityOnCart = 0; // TODO: compute this from cartEntries
  const availableCartQuantity = MAX_CART_QUANTITY - quantityOnCart;
  const maxCartQuantityMet = productQuantity > availableCartQuantity;

  const { formattedTotalPrice } = useProductTotalPrice(
    selectedProduct,
    slotsSelections,
    productQuantity,
    cartIdEditing,
    rewardBenefitId
  );

  const buttonLabel = useMemo(() => {
    if (maxCartQuantityMet) {
      return formatMessage({ id: 'limitReached' });
    }

    const addToOrderText = formatMessage({
      id: cartIdEditing ? 'update' : 'addToCart',
    });
    return `${addToOrderText} - ${formattedTotalPrice}`;
  }, [cartIdEditing, formatMessage, formattedTotalPrice, maxCartQuantityMet]);

  const handleAddToCart = () => {
    addToCart(selectedProduct, productQuantity, slotsSelections);
    if (cartIdEditing) {
      closeWizard();
    } else {
      navigate(routes.menu);
    }
  };

  const handleQuantityUpdate = (value: number) => {
    setProductQuantity((prevValue: number) => {
      const action = prevValue < value ? 'increase' : 'decrease';
      logButtonClick(
        {
          attributes: {
            component: ClickEventComponentNames.BUTTON,
            name: 'Product Incrementor',
          },
        },
        { logDuplicateClickEvent: true }
      );
      logRBIEvent(
        {
          name: CustomEventNames.ITEM_QUANTITY_MODIFIED,
          type: EventTypes.Other,
          attributes: {
            increaseOrDecrease: action,
            totalQuantity: value,
          },
        },
        { skipLoggingToBraze: true }
      );
      return value;
    });
  };

  const isDisabled = maxCartQuantityMet;
  // FIXME: disallow changing quantity for rewards incentives - CartV2 and Hydration can't handle it yet
  const isEditingCartEntryWithReward = appliedLoyaltyRewards[cartIdEditing ?? ''];
  const showIncrementor = !maxCartQuantityMet && !isEditingCartEntryWithReward && !offerId;

  const disableIncrementQuantity = maxCartQuantityMet;
  const disableIncrementor = maxCartQuantityMet;

  const productName = selectedProduct.name?.locale ?? '';
  const ariaLabels = useMemo(
    () => ({
      ariaLabel: formatMessage(
        { id: cartIdEditing ? 'updateQuantityAndItemWithPrice' : 'addQuantityAndItemWithPrice' },
        { itemName: productName, quantity: productQuantity, price: formattedTotalPrice }
      ),
      decrementAriaLabel: formatMessage({ id: 'decrementAriaLabel' }, { productName }),
      incrementAriaLabel: formatMessage({ id: 'incrementAriaLabel' }, { productName }),
      quantityAriaLabel: formatMessage(
        { id: 'quantityAriaLabel' },
        { productQuantity, productName }
      ),
    }),
    [cartIdEditing, formatMessage, formattedTotalPrice, productName, productQuantity]
  );

  return (
    <>
      {showIncrementor && (
        <NumberInput
          displayedValue={productQuantity}
          value={productQuantity}
          onChange={handleQuantityUpdate}
          min={MIN_CART_QUANTITY}
          max={availableCartQuantity}
          hideNumberInput
          disableIncrement={disableIncrementQuantity}
          disabled={disableIncrementor}
          ariaLabel={ariaLabels.quantityAriaLabel}
          decrementAriaLabel={ariaLabels.decrementAriaLabel}
          incrementAriaLabel={ariaLabels.incrementAriaLabel}
          displayValueStyleProps={displayValueStyleProps}
          incrementorStyleProps={incrementorStyleProps}
          hapticFeedbackStyle={HapticsImpactStyle.Light}
        />
      )}
      <ActionButton
        accessibilityLabel={ariaLabels.ariaLabel}
        testID="product-home-action-button"
        disabled={isDisabled}
        onPress={handleAddToCart}
        flex={1}
        marginLeft={showIncrementor ? '$6' : '0'}
      >
        {buttonLabel}
      </ActionButton>
    </>
  );
};
